import React, { useState, useRef, useCallback, useEffect } from "react";

import {
  FileImage,
  Film,
  Music,
  Play,
  Pause,
  Check,
  Copy,
  Link2,
  SendHorizonal,
} from "lucide-react";
import Masonry from "react-masonry-css";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { MediaResult } from "@/lib/agent/payload-parser";
import { formatDuration, handleImageRetry, getProviderUrl, getProviderDisplayName, getAuthorUrl, getAttributionText } from "@/components/editor/media-library-panel/media-utils";

interface MediaLibraryResultsHITLProps {
  mediaType: "image" | "video" | "audio";
  provider: string;
  mediaResults: MediaResult[];
  summary: string;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
}

export function MediaLibraryResultsHITL({
  mediaType,
  provider,
  mediaResults,
  summary,
  onAction,
  messageId,
}: MediaLibraryResultsHITLProps): React.ReactElement {
  const [selectedMedia, setSelectedMedia] = useState<string[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<MediaResult | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // Audio playback state
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.ontimeupdate = null;
      audioRef.current = null;
    }
    setPlayingId(null);
    setCurrentTime(0);
    setAudioDuration(0);
  }, []);

  const handlePlayPause = useCallback(
    (e: React.MouseEvent, media: MediaResult) => {
      e.stopPropagation();
      if (playingId === media.id) {
        stopAudio();
        return;
      }
      stopAudio();
      const audio = new Audio(media.previewUrl);
      audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
      audio.onloadedmetadata = () => setAudioDuration(audio.duration);
      audio.onended = () => stopAudio();
      audio.onerror = () => stopAudio();
      audio.play();
      audioRef.current = audio;
      setPlayingId(media.id);
      setAudioDuration(media.duration || 0);
    },
    [playingId, stopAudio],
  );

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const handleToggleSelection = (
    e: React.MouseEvent,
    mediaId: string,
  ): void => {
    e.stopPropagation(); // Prevent opening window when clicking checkbox area
    setSelectedMedia((prev) =>
      prev.includes(mediaId)
        ? prev.filter((id) => id !== mediaId)
        : [...prev, mediaId],
    );
  };

  const handleSelectAll = (): void => {
    if (selectedMedia.length === mediaResults.length) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(mediaResults.map((m) => m.id));
    }
  };

  const handleSelectImages = (): void => {
    setIsSubmitted(true);

    // Build structured info for each selected media item
    const selectedItems = mediaResults
      .filter((m) => selectedMedia.includes(m.id))
      .map((m) => ({
        provider: m.provider,
        mediaId: m.id,
        type: mediaType,
        title: m.title,
        downloadUrl: m.downloadUrl || m.previewUrl,
      }));

    onAction?.(
      `select_media:${JSON.stringify(selectedItems)}`,
      messageId || "",
    );

    // Reset after short delay so user can select more images later
    setTimeout(() => {
      setIsSubmitted(false);
      setSelectedMedia([]);
    }, 2000);
  };

  const getMediaTypeIcon = (): React.ReactElement => {
    switch (mediaType) {
      case "image":
        return <FileImage className="w-4 h-4" />;
      case "video":
        return <Film className="w-4 h-4" />;
      case "audio":
        return <Music className="w-4 h-4" />;
    }
  };

  return (
    <div className="w-full min-w-0 max-w-[calc(100vw-3rem)] sm:max-w-5xl bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-border space-y-2 sm:space-y-3 overflow-hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getMediaTypeIcon()}
            <span className="text-sm font-medium">
              {mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} Results
            </span>
          </div>
          {provider !== "all" && (
            <div className="text-xs text-muted-foreground">
              {provider.charAt(0).toUpperCase() + provider.slice(1)}
            </div>
          )}
        </div>

        {summary && (
          <p className="text-xs text-muted-foreground truncate">{summary}</p>
        )}

        {/* Selection controls */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSelectAll}
            className="text-xs text-primary hover:underline"
          >
            {selectedMedia.length === mediaResults.length
              ? "Deselect All"
              : "Select All"}
          </button>
          <span className="text-xs text-muted-foreground">
            {selectedMedia.length} of {mediaResults.length} selected
          </span>
        </div>
      </div>

      {/* Content area */}
      <div className="max-h-52 sm:max-h-96 overflow-y-auto">
        {mediaType === "audio" ? (
          /* Audio: Playlist-style list */
          <div className="flex flex-col overflow-hidden">
            {mediaResults.map((media) => {
              const isSelected = selectedMedia.includes(media.id);
              const isPlaying = playingId === media.id;
              const progress =
                isPlaying && audioDuration > 0
                  ? (currentTime / audioDuration) * 100
                  : 0;

              return (
                <div
                  key={media.id}
                  onClick={(e) => handleToggleSelection(e, media.id)}
                  className={cn(
                    "group relative flex flex-col transition-colors cursor-pointer overflow-hidden",
                    "hover:bg-muted/50",
                    isSelected && "bg-primary/5",
                    isPlaying && "bg-purple-500/5",
                  )}
                >
                  <div className="flex items-center gap-2 sm:gap-2.5 px-2 sm:px-3 py-1.5 sm:py-2 overflow-hidden">
                    {/* Play/Pause button */}
                    <button
                      onClick={(e) => handlePlayPause(e, media)}
                      className={cn(
                        "flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all",
                        isSelected && !isPlaying
                          ? "bg-primary text-primary-foreground"
                          : isPlaying
                            ? "bg-purple-500/20 text-purple-400"
                            : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground",
                      )}
                      aria-label={isPlaying ? "Pause" : "Play"}
                    >
                      {isSelected && !isPlaying ? (
                        <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      ) : isPlaying ? (
                        <Pause className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      ) : (
                        <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5 ml-0.5" />
                      )}
                    </button>

                    {/* Title + meta */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p
                        className="text-xs sm:text-[13px] font-medium leading-tight truncate max-w-lg"
                        title={media.title}
                      >
                        {media.title}
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[10px] sm:text-[11px] text-muted-foreground truncate">
                          {media.author && media.authorUrl ? (
                            <a
                              href={getAuthorUrl(media.provider, media.authorUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-foreground underline"
                            >
                              {getAttributionText(media.provider, media.author, "audio")}
                            </a>
                          ) : (
                            <a
                              href={getProviderUrl(media.provider)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-foreground underline"
                            >
                              {getAttributionText(media.provider, media.author || "", "audio")}
                            </a>
                          )}
                        </span>
                        {media.duration != null && (
                          <>
                            <span className="text-[10px] sm:text-[11px] text-muted-foreground">
                              ·
                            </span>
                            <span
                              className={cn(
                                "text-[10px] sm:text-[11px] tabular-nums flex-shrink-0",
                                isPlaying
                                  ? "text-purple-400"
                                  : "text-muted-foreground",
                              )}
                            >
                              {isPlaying
                                ? `${formatDuration(currentTime)} / ${formatDuration(media.duration)}`
                                : formatDuration(media.duration)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                  </div>

                  {/* Progress bar */}
                  {isPlaying && (
                    <div className="h-0.5 bg-muted/50 mx-2 sm:mx-3 mb-1 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-400 rounded-full transition-[width] duration-300 ease-linear"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Images/Videos: Masonry Grid */
          <div className="p-2 sm:p-4">
            <Masonry
              breakpointCols={{
                default: 4,
                1024: 3,
                640: 2,
              }}
              className="flex -ml-1.5 sm:-ml-2 w-auto"
              columnClassName="pl-1.5 sm:pl-2 bg-clip-padding"
            >
              {mediaResults.map((media) => {
                const isSelected = selectedMedia.includes(media.id);

                return (
                  <div
                    key={media.id}
                    onClick={() => setPreviewMedia(media)}
                    className={cn(
                      "relative overflow-hidden rounded-md sm:rounded-lg group mb-1 sm:mb-1.5 cursor-pointer",
                      isSelected &&
                        "ring-1 ring-primary ring-offset-1 ring-offset-background",
                    )}
                    style={
                      media.width && media.height
                        ? { aspectRatio: `${media.width}/${media.height}` }
                        : undefined
                    }
                  >
                    {/* Selection dot */}
                    <button
                      onClick={(e) => handleToggleSelection(e, media.id)}
                      className={cn(
                        "absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center z-40",
                        "transition-all duration-200",
                        isSelected
                          ? "bg-primary shadow-sm"
                          : "bg-black/30 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-black/50",
                      )}
                      aria-label={isSelected ? "Deselect" : "Select"}
                    >
                      {isSelected ? (
                        <Check className="w-3 h-3 text-primary-foreground" />
                      ) : (
                        <div className="w-2 h-2 rounded-full border-[1.5px] border-white" />
                      )}
                    </button>

                    <img
                      src={media.previewUrl}
                      alt={media.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => handleImageRetry(e, media.previewUrl)}
                    />

                    {/* Always-visible 2-line attribution: provider (+ duration) top, author bottom */}
                    <div className="absolute bottom-0 left-0 right-0 pt-6 pb-1 px-1.5 bg-gradient-to-t from-black/55 via-black/25 to-transparent z-30">
                      <div className="flex items-center justify-between gap-0.5">
                        <a
                          href={getProviderUrl(media.provider)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-[9px] text-white/60 font-medium leading-tight drop-shadow-sm hover:text-white/80 transition-colors truncate"
                        >
                          {getProviderDisplayName(media.provider)}
                        </a>
                        {mediaType === "video" && media.duration != null && (
                          <span className="text-[10px] text-white/90 font-semibold tabular-nums shrink-0 drop-shadow-sm">
                            {formatDuration(media.duration)}
                          </span>
                        )}
                      </div>
                      {media.author && (
                        <a
                          href={media.authorUrl ? getAuthorUrl(media.provider, media.authorUrl) : getProviderUrl(media.provider)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="block text-[10px] text-white/90 font-medium leading-tight drop-shadow-sm hover:text-white transition-colors truncate"
                        >
                          {media.author}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </Masonry>
          </div>
        )}
      </div>

      {/* Use Selected Button */}
      <div className="p-3 sm:p-4 border-t border-border">
        <Button
          variant="default"
          size="sm"
          onClick={handleSelectImages}
          disabled={selectedMedia.length === 0 || isSubmitted}
          className="w-full"
        >
          {isSubmitted ? (
            <>
              <Check className="w-3.5 h-3.5 mr-1.5" />
              Sent to Agent
            </>
          ) : (
            <>
              <SendHorizonal className="w-3.5 h-3.5 mr-1.5" />
              {`Use the ${selectedMedia.length} selected ${mediaType === "audio" ? "audio" : mediaType === "video" ? "videos" : "images"}`}
            </>
          )}
        </Button>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewMedia !== null} onOpenChange={(open) => { if (!open) setPreviewMedia(null); }}>
        {previewMedia && (() => {
          const previewSrc = previewMedia.downloadUrl || previewMedia.previewUrl;
          const attribution = getAttributionText(previewMedia.provider, previewMedia.author || "", mediaType);
          return (
            <DialogContent className="max-w-[92vw] sm:max-w-3xl max-h-[85dvh] p-0 overflow-hidden bg-black/95 border-border gap-0 rounded-lg">
              <DialogTitle className="sr-only">{previewMedia.title}</DialogTitle>
              <div className="relative flex items-center justify-center">
                {mediaType === "video" ? (
                  <video
                    src={previewSrc}
                    poster={previewMedia.previewUrl}
                    controls
                    autoPlay
                    className="w-full object-contain max-h-[60dvh]"
                    playsInline
                  />
                ) : (
                  <img
                    src={previewSrc}
                    alt={previewMedia.title}
                    className="w-full object-contain max-h-[60dvh]"
                  />
                )}
              </div>
              <div className="px-4 py-3 bg-background space-y-2 overflow-hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{previewMedia.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {previewMedia.author && previewMedia.authorUrl ? (
                        <a
                          href={getAuthorUrl(previewMedia.provider, previewMedia.authorUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-foreground underline"
                        >
                          {attribution}
                        </a>
                      ) : (
                        attribution
                      )}
                      {mediaType === "video" && previewMedia.duration != null && ` · ${formatDuration(previewMedia.duration)}`}
                      {previewMedia.width && previewMedia.height && ` · ${previewMedia.width}×${previewMedia.height}`}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleSelection(e, previewMedia.id);
                    }}
                    disabled={isSubmitted}
                    className={cn(
                      "flex items-center gap-1.5 rounded-full text-xs font-medium transition-colors shrink-0",
                      "px-3.5 py-1.5",
                      selectedMedia.includes(previewMedia.id)
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80",
                    )}
                  >
                    {selectedMedia.includes(previewMedia.id) && (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    {selectedMedia.includes(previewMedia.id) ? "Selected" : "Select"}
                  </button>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/40 border border-border/50 overflow-hidden">
                  <Link2 className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
                  <a
                    href={previewSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 min-w-0 text-xs text-muted-foreground/70 hover:text-foreground transition-colors truncate font-mono"
                    title={previewSrc}
                  >
                    {previewSrc}
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0 hover:bg-muted-foreground/10"
                    onClick={() => {
                      navigator.clipboard.writeText(previewSrc);
                      setIsCopied(true);
                      setTimeout(() => setIsCopied(false), 1200);
                    }}
                  >
                    {isCopied ? (
                      <Check className="w-3 h-3 text-primary" />
                    ) : (
                      <Copy className="w-3 h-3 text-muted-foreground/50" />
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          );
        })()}
      </Dialog>
    </div>
  );
}
